import React, { useRef, useEffect, useState, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { useAppSelector } from '../../../../../../../../app/hooks';
import { useAskAica } from '../../../../../../../../store/dashboard/askAica';
import { AICA_ASK_AICA_MESSAGE_SENT } from '../../../../../../../../utils/constants/mixpanelEvents/investorEvents';
import { Mixpanel } from '../../../../../../../../utils/mixpanel';
import { getCompanyLogo, getFaviconUrl } from '../../../../../../../../utils/urlMetadata';

import AskAicaInputBox from '../AskAicaInputBox/AskAicaInputBox';
import { Button } from '../../../../../../../../common/_custom';
import CopyIcon from '../../../../../../../../assets/Svg/CopyIcon';
import TickIcon from '../../../../../../../../assets/Svg/TickIcon';
import MessagesContainer from '../MessagesContainer/MessagesContainer';
import useCopyText from '../../../../../../../../common/_custom/Hooks/useCopyText/useCopyText';
import { MessageType, COPY_TARGET } from '../../../../../../../../store/dashboard/askAica/constants';
import { AskAicaFileRef } from '../../hooks/useAskAicaFileActions';

import styles from '../../AskAicaV2.module.scss';

interface LeftPanelProps {
    selectedCompany?: any;
    overviewData?: any;
    onSendMessageRef?: React.MutableRefObject<((message: string, sources?: string[], flowKey?: string) => void) | null>;
    onClearChat: () => void;
    /** Opens a generated file in the matching in-app viewer (docx/xlsx/pdf). */
    onOpenFile: (file: AskAicaFileRef) => void;
    /** Snapshots a generated file into Reports (docx/xlsx/txt). Absent when the reports.addToReports visibility key is off. */
    onAddFileToReports?: (file: AskAicaFileRef) => void;
}

const LeftPanel: React.FC<LeftPanelProps> = ({
    selectedCompany,
    overviewData,
    onSendMessageRef,
    onClearChat,
    onOpenFile,
    onAddFileToReports,
}) => {
    const user = useAppSelector((state) => state.user.userData);
    const isClearingChat = useAppSelector((state) => state.dashboard.askAicaReducer.isClearingChat);
    const params = useParams();
    const {
        messages,
        isLoading,
        isLoadingChatHistory,
        sendMessage,
        registerStreamStartedCallback,
        messagesEndRef,
        fetchChatHistory,
        hasMoreData,
    } = useAskAica();
    const [streamStarted, setStreamStarted] = useState(false);
    const [shouldScrollToBottom, setShouldScrollToBottom] = useState(false);
    const initialFetchRef = useRef(true);
    const orgId = params.id ? params.id : user?.investorOrgId;

    // Register the streamStarted callback with useAskAica
    useEffect(() => {
        registerStreamStartedCallback(setStreamStarted);
    }, [registerStreamStartedCallback]);

    // Reset scroll flag after scrolling is triggered
    useEffect(() => {
        if (shouldScrollToBottom) {
            // Reset the flag after a short delay to allow the scroll to complete
            const timer = setTimeout(() => {
                setShouldScrollToBottom(false);
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [shouldScrollToBottom]);

    // Get company logo with fallback logic (same as Details component)
    const getCompanyLogoSrc = () => {
        const companyLogo = selectedCompany?.logo;
        const websiteUrl = overviewData?.socialInfo?.websiteUrl;
        const logoFromWebsite = websiteUrl && getCompanyLogo(websiteUrl);
        const faviconFromWebsite = websiteUrl && getFaviconUrl(websiteUrl);
        return companyLogo || logoFromWebsite || faviconFromWebsite;
    };

    const handleSendMessage = useCallback(
        (message: string, _sources?: string[], flowKey?: string) => {
            // track mixpanel event
            Mixpanel.track(AICA_ASK_AICA_MESSAGE_SENT, {
                messageLength: message.length,
                userId: user?.userId,
                organizationId: orgId,
            });

            // Reset stream started flag when sending a new message
            setStreamStarted(false);
            // Trigger scroll to bottom immediately when user sends a message
            setShouldScrollToBottom(true);
            // Call the useAskAica hook's sendMessage function
            return sendMessage(message, flowKey);
        },
        [user?.userId, orgId, sendMessage],
    );

    // Expose the handleSendMessage function to parent component
    useEffect(() => {
        if (onSendMessageRef) {
            onSendMessageRef.current = handleSendMessage;
        }

        // Cleanup ref on unmount or when handleSendMessage changes
        return () => {
            if (onSendMessageRef) {
                onSendMessageRef.current = null;
            }
        };
    }, [onSendMessageRef, handleSendMessage]);

    const retryMessage = (index) => {
        const messageToRetry = messages[index + 1];
        if (messageToRetry) {
            handleSendMessage(messageToRetry.content);
        }
    };

    const messagesContainerRef = useRef<HTMLDivElement>(null);
    const { copiedId, copyHtmlContent } = useCopyText();

    /**
     * Copies the full chat content in chronological order (oldest first).
     * Assembles HTML and plain text from rendered DOM elements using data attributes.
     */
    const handleCopyFullChat = useCallback(() => {
        if (!messagesContainerRef.current) return;
        const messageEls = Array.from(messagesContainerRef.current.querySelectorAll('[data-message-type]')).reverse() as HTMLElement[];
        let html = '';
        let plain = '';
        messageEls.forEach((el) => {
            const type = el.getAttribute('data-message-type');
            if (type === MessageType.USER) {
                const bubble = el.querySelector('p') as HTMLElement | null;
                if (!bubble) return;
                html += `<p><b>You:</b> ${bubble.innerHTML}</p>`;
                plain += `You: ${bubble.innerText}\n\n`;
                return;
            }
            const content = el.querySelector('[data-content-role="assistant"]') as HTMLElement | null;
            if (!content) return;
            html += `<p><b>AICA:</b></p>${content.innerHTML}<br/>`;
            plain += `AICA:\n${content.innerText}\n\n`;
        });
        copyHtmlContent(html, plain.trim(), COPY_TARGET.FULL_CHAT);
    }, [copyHtmlContent]);

    const logoSrc = getCompanyLogoSrc();

    return (
        <div className={styles.LeftPanel}>
            {/* Header with Company/User Info */}
            <div className={styles.Header}>
                <div className={styles.CompanyInfoContainer}>
                    <div className={styles.CompanyLogo}>
                        {logoSrc ? (
                            <img
                                src={logoSrc}
                                alt="Company Logo"
                                onError={(e) => {
                                    e.currentTarget.style.display = 'none';
                                    const parent = e.currentTarget.parentElement;
                                    if (parent && !parent.querySelector(`.${styles.PlaceholderLogo}`)) {
                                        const placeholder = document.createElement('div');
                                        placeholder.className = styles.PlaceholderLogo;
                                        placeholder.textContent = selectedCompany?.orgName?.charAt(0) || user?.userName?.charAt(0) || 'U';
                                        parent.appendChild(placeholder);
                                    }
                                }}
                            />
                        ) : (
                            <div className={styles.PlaceholderLogo}>
                                {selectedCompany?.orgName?.charAt(0) || user?.userName?.charAt(0) || 'U'}
                            </div>
                        )}
                    </div>
                    <h2 className={styles.CompanyName}>{selectedCompany?.orgName || user?.userName || 'User'}</h2>
                </div>
                <div className={styles.HeaderActions}>
                    <button
                        className={styles.CopyFullChatButton}
                        onClick={handleCopyFullChat}
                        title="Copy full chat"
                        disabled={!messages.length}
                    >
                        {copiedId === COPY_TARGET.FULL_CHAT ? (
                            <TickIcon width={14} height={14} color="#49A71B" />
                        ) : (
                            <CopyIcon width="14" height="14" colour="#6b7280" />
                        )}
                    </button>
                    <Button
                        variant="white"
                        size="tiny"
                        text="Clear Chat"
                        style={{ margin: '0' }}
                        textstyle={{ fontSize: '0.75rem' }}
                        onClick={onClearChat}
                        disabled={isClearingChat}
                    />
                </div>
            </div>

            {/* Loading More Messages Overlay */}
            {isLoadingChatHistory && !initialFetchRef.current && (
                <div className={styles.LoadingOverlay}>
                    <div className={styles.LoadingContent}>
                        <div className={styles.LoadingText}>Loading more messages</div>
                        <div className={styles.TypingIndicator}>
                            <div className={styles.TypingDot}></div>
                            <div className={styles.TypingDot}></div>
                            <div className={styles.TypingDot}></div>
                        </div>
                    </div>
                </div>
            )}

            {/* Chat Area */}
            <div className={styles.ChatArea}>
                <MessagesContainer
                    streamStarted={streamStarted}
                    isLoadingChatHistory={isLoadingChatHistory}
                    messagesEndRef={messagesEndRef}
                    fetchChatHistory={fetchChatHistory}
                    hasMoreData={hasMoreData}
                    retryMessage={retryMessage}
                    scrollToBottom={shouldScrollToBottom}
                    initialFetchRef={initialFetchRef}
                    containerRef={messagesContainerRef}
                    onOpenFile={onOpenFile}
                    onAddFileToReports={onAddFileToReports}
                />
                <AskAicaInputBox onSendMessage={handleSendMessage} isLoading={isLoading} />
            </div>
        </div>
    );
};

export default LeftPanel;
